import { Button } from "@/components/common/Button/Button"
import { FlyingSparklingBirdIcon } from "@/components/common/icons/FlyingSparklingBirdIcon/FlyingSparklingBirdIcon"
import { ModalBase } from "@/components/common/ModalBase/ModalBase"
import { Typography } from "@/components/common/Typography/Typography"
import useFreeTrial from "@/hooks/useFreeTrial"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { TRIAL_EXPIRED_EVENT } from "@/utils/limitErrors"
import { requestInAppUpgrade } from "@/utils/upgradeEntry"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"

// Shown at most once per user and phase. The key carries the user id because
// localStorage is shared by every account used in this browser.
const UPGRADE_MODAL_STORAGE_KEY = "turnus-upgrade-modal-shown"
// The modal used to write this one flag for the whole browser. It only ever
// showed the last-day phase, so it still counts as that phase shown: people who
// dismissed it must not see it again because the key now carries the user id.
const LEGACY_ENDING_STORAGE_KEY = "turnus-upgrade-modal-shown"
const UPGRADE_MODAL_EXPIRED_STORAGE_KEY = "turnus-upgrade-modal-expired-shown"
const UPGRADE_MODAL_SHOW_ON_LEFT_DAYS = 1

type UpgradeModalPhase = "ending" | "expired"

const storageKeyFor = (phase: UpgradeModalPhase, userId: string) =>
  `${phase === "expired" ? UPGRADE_MODAL_EXPIRED_STORAGE_KEY : UPGRADE_MODAL_STORAGE_KEY}:${userId}`

function hasKey(key: string): boolean {
  try {
    return localStorage.getItem(key) !== null
  } catch {
    return false
  }
}

const wasShown = (phase: UpgradeModalPhase, key: string): boolean =>
  hasKey(key) || (phase === "ending" && hasKey(LEGACY_ENDING_STORAGE_KEY))

function markShown(key: string): void {
  try {
    localStorage.setItem(key, "true")
  } catch {
    // storage unavailable: the modal can reappear, which is harmless
  }
}

interface UpgradeModalViewProps {
  onClose: () => void
  /** The upgrade button was pressed: opens /upgrade. */
  onUpgrade: () => void
  translations: {
    title: string
    description: string
    primaryAction: string
  }
}

export const UpgradeModalView: React.FC<UpgradeModalViewProps> = ({
  onClose,
  onUpgrade,
  translations,
}) => {
  useEffect(() => {
    trackEvent(AnalyticsEvent.UPGRADE_MODAL_SHOWN)
  }, [])

  return (
    <ModalBase showSidebarLeft onClose={onClose} modalTestId="upgrade-modal">
      <div className="flex min-h-full flex-col items-center justify-center gap-8">
        <FlyingSparklingBirdIcon />
        <div className="flex max-w-[500px] flex-col gap-4 text-center">
          <Typography variant="titleM">{translations.title}</Typography>
          <Typography variant="textM">{translations.description}</Typography>
        </div>
        <Button
          label=""
          title={translations.primaryAction}
          variantSize="medium"
          variantColor="yellow"
          onClick={onUpgrade}
        />
      </div>
    </ModalBase>
  )
}

export const UpgradeModal: React.FC = () => {
  const { translate } = useTranslation()
  const { left, isExpired } = useFreeTrial()
  const { user } = useUser()
  const queryClient = useQueryClient()
  const [closedKeys, setClosedKeys] = useState<string[]>([])

  // A trial-expiry 403 can arrive before the cached trial status turns expired
  // (tab left open across the end of the trial). It only asks for a fresh
  // status: the expired phase, and the shown flag it writes, follow a confirmed
  // status, never the 403 alone.
  useEffect(() => {
    const onTrialExpired = () => {
      void queryClient.invalidateQueries({
        queryKey: ["userFreeTrialsLeftData"],
      })
    }
    window.addEventListener(TRIAL_EXPIRED_EVENT, onTrialExpired)
    return () => window.removeEventListener(TRIAL_EXPIRED_EVENT, onTrialExpired)
  }, [queryClient])

  const phase = getPhase(isExpired, left)
  if (!phase || !user?.id) {
    return undefined
  }

  const storageKey = storageKeyFor(phase, user.id)
  if (closedKeys.includes(storageKey) || wasShown(phase, storageKey)) {
    return undefined
  }

  const handleClose = () => {
    markShown(storageKey)
    setClosedKeys((keys) => [...keys, storageKey])
  }

  // Going to the upgrade page is an answer to the modal as much as closing it is
  const handleUpgrade = () => {
    handleClose()
    requestInAppUpgrade()
  }

  const translations =
    phase === "expired"
      ? {
          title: translate("FREE_TRIAL/ENDED/ENDED"),
          description: translate("UPGRADE_MODAL/EXPIRED_DESCRIPTION"),
          primaryAction: translate("UPGRADE_MODAL/PRIMARY_ACTION"),
        }
      : {
          title: translate("UPGRADE_MODAL/TITLE"),
          description: translate("UPGRADE_MODAL/DESCRIPTION"),
          primaryAction: translate("UPGRADE_MODAL/PRIMARY_ACTION"),
        }

  return (
    <UpgradeModalView
      onClose={handleClose}
      onUpgrade={handleUpgrade}
      translations={translations}
    />
  )
}

function getPhase(isExpired: boolean, left: number): UpgradeModalPhase | null {
  if (isExpired) return "expired"
  if (left <= UPGRADE_MODAL_SHOW_ON_LEFT_DAYS && left > 0) return "ending"
  return null
}
